import { DateTime as LuxonDateTime } from 'luxon';
import { fetchAnalystRatings } from '../../AnalystRatingCalendar/Services';
import AnalystRatingsColumnsDef from '../../AnalystRatingCalendar/ColumnsDef';
import AnalystRatingsColumnsDefV2 from '../../AnalystRatingCalendar/ColumnsDefV2';
import { fetchConferenceCall } from '../../ConferenceCallCalendar/Services';
import ConferenceCallCalendarDef from '../../ConferenceCallCalendar/ColumnsDef';
import { fetchDividends } from '../../DividendsCalendar/Services';
import DividendsCalendarColumnsDef from '../../DividendsCalendar/ColumnsDef';
import { fetchEarnings } from '../../EarningsCalendar/Services';
import EarningsCalendarColumnsDefinition from '../../EarningsCalendar/ColumnsDef';
import { fetchEconomics } from '../../EconomicsCalendar/Services';
import EconomicsColDef from '../../EconomicsCalendar/ColumnsDef';
import { COMPANY_EVENTS_PAGE_SIZE } from '../../EventsCalendar/constants';
import { fetchCompanyEvents } from '../../EventsCalendar/Services';
import EventsCalendarColumnsDef from '../../EventsCalendar/ColumnsDef';
import { fetchGuidance } from '../../GuidanceCalendar/Services';
import GuidanceCalendarColumnsDef from '../../GuidanceCalendar/ColumnsDef';
import { fetchIPO } from '../../IPOCalendar/Services';
import IPOColumnsDef from '../../IPOCalendar/ColumnsDef';
import { CalendarDataI, CalendarType, IDateRange, ServerSideFetchOptions } from '../interfaces';
import { formatInterval, formatIntervalLastOpenMarketDay, formatIntervalQueryParams, getLastOpenMarketDay } from '.';
import { fetchMA } from '../../MACalendar/Services';
import MACalendarColumnsDef from '../../MACalendar/ColumnsDef';
import { fetchOptions } from '../../OptionsCalendar/Services';
import OptionsCalendarColumnsDef from '../../OptionsCalendar/ColumnsDef';
import { fetchSpac } from '../../SpacCalendar/Services';
import SPACColumnsDefinition from '../../SpacCalendar/ColumnsDef';
import { fetchStockSplits } from '../../StockSplitsCalendar/Services';
import StockSplitsColumnsDef from '../../StockSplitsCalendar/ColumnsDef';
import { ISetParams, Ratings, RatingsCalendarParams, SplitsCalendarParams } from '@benzinga/calendar-manager';
import { fetchInsiderTrades, InsiderTradesParams } from '../../InsiderTradesCalendar/Services';
import InsiderTradesCalendarColumnsDef from '../../InsiderTradesCalendar/ColumnsDefs/ColumnsDef';
import InsiderTradesCalendarColumnsDefV2 from '../../InsiderTradesCalendar/ColumnsDefs/ColumnsDefV2';
import InsiderTradesNetWorthColumnsDef from '../../InsiderTradesCalendar/ColumnsDefs/NetWorthColumnsDef';
import { fetchShortInterest } from '../../ShortInterestCalendar/Services';
import ShortInterestCalendarColumnDef from '../../ShortInterestCalendar/ColumnsDef';
import { fetchFDA } from '../../FDACalendar/Services';
import FDAColumnsDef from '../../FDACalendar/ColumnsDef';
import type { FDACalendarTabKeysType } from '../../FDACalendar/Services';
import { NewsFeedQueryParams } from '@benzinga/basic-news-manager';
import { getNumberSuffix } from '@benzinga/utils';

const loadAnalystRatingCalendar = () =>
  import('../../AnalystRatingCalendar').then(module => ({ default: module.AnalystRatingCalendar }));
const loadConferenceCallCalendar = () =>
  import('../../ConferenceCallCalendar').then(module => ({ default: module.ConferenceCallCalendar }));
const loadDividendsCalendar = () =>
  import('../../DividendsCalendar').then(module => ({ default: module.DividendsCalendar }));
const loadEarningsCalendar = () =>
  import('../../EarningsCalendar').then(module => ({ default: module.EarningsCalendar }));
const loadEconomicsCalendar = () =>
  import('../../EconomicsCalendar').then(module => ({ default: module.EconomicsCalendar }));
const loadEventsCalendar = () => import('../../EventsCalendar').then(module => ({ default: module.EventsCalendar }));
const loadGuidanceCalendar = () =>
  import('../../GuidanceCalendar').then(module => ({ default: module.GuidanceCalendar }));
const loadIPOCalendar = () => import('../../IPOCalendar').then(module => ({ default: module.IPOCalendar }));
const loadMACalendar = () => import('../../MACalendar').then(module => ({ default: module.MACalendar }));
const loadOptionsCalendar = () => import('../../OptionsCalendar').then(module => ({ default: module.OptionsCalendar }));
const loadSpacCalendar = () => import('../../SpacCalendar').then(module => ({ default: module.SpacCalendar }));
const loadStockSplitsCalendar = () =>
  import('../../StockSplitsCalendar').then(module => ({ default: module.StockSplitsCalendar }));
const loadInsiderTradesCalendar = () =>
  import('../../InsiderTradesCalendar').then(module => ({ default: module.InsiderTradesCalendar }));
const loadShortInterestCalendar = () =>
  import('../../ShortInterestCalendar').then(module => ({ default: module.ShortInterestCalendar }));
const loadFDACalendar = () => import('../../FDACalendar').then(module => ({ default: module.FDACalendar }));

const calendarQueryParams = (params?: ServerSideFetchOptions['params']): ISetParams => (params ?? {}) as ISetParams;

const hasCalendarSymbols = (params?: ServerSideFetchOptions['params']) => {
  const query = calendarQueryParams(params);
  return Boolean(query.symbols ?? (query as { securities?: string }).securities);
};

const toQueryDateString = (value: string | Date | undefined, fallback?: string): string | undefined => {
  if (typeof value === 'string') return value;
  if (value instanceof Date) return value.toISOString().split('T')[0];
  return fallback;
};

const symbolFromQueryParams = (queryParams: ISetParams): string => {
  const { symbols } = queryParams;
  if (typeof symbols === 'string') return symbols;
  if (Array.isArray(symbols)) return symbols[0] ?? '';
  return '';
};

const jsDateString = new Date().toISOString().split('T')[0];
const now = LuxonDateTime.fromFormat(jsDateString, 'yyyy-MM-dd');
const todayDateStroke = now.toFormat('MMM d').concat(`${getNumberSuffix(now.day)}`);

const SevenDaysBeforeInterval: IDateRange = formatIntervalLastOpenMarketDay(now.minus({ days: 7 }), now);

const getSevenDaysBeforeInterval = (): IDateRange => {
  const dateNow = LuxonDateTime.fromFormat(new Date().toISOString().split('T')[0], 'yyyy-MM-dd');
  return formatIntervalLastOpenMarketDay(dateNow.minus({ days: 7 }), dateNow);
};
const SevenDaysAfterInterval: IDateRange = formatIntervalLastOpenMarketDay(now, now.plus({ days: 7 }));

const ThreeMonthsForwardInterval: IDateRange = formatIntervalLastOpenMarketDay(now, now.plus({ months: 3 }));

const ThirtyDaysBeforeInterval: IDateRange = formatIntervalLastOpenMarketDay(now.minus({ days: 30 }), now);
const ThirtyDaysAfterInterval: IDateRange = formatIntervalLastOpenMarketDay(now, now.plus({ days: 30 }));

const TwoMonthsAfterInterval: IDateRange = formatIntervalLastOpenMarketDay(now, now.plus({ months: 2 }));

const OneMonthBefore: IDateRange = formatIntervalLastOpenMarketDay(now.minus({ months: 1 }), now);
const OneMonthAfter: IDateRange = formatIntervalLastOpenMarketDay(now, now.plus({ months: 1 }));

const TwoMonthsBefore: IDateRange = formatIntervalLastOpenMarketDay(now.minus({ months: 2 }), now);

const FiveYearsBeforeInterval: IDateRange = formatIntervalLastOpenMarketDay(now.minus({ years: 5 }), now);

const sevenDaysBeforeDateStroke = now.minus({ days: 7 });
const oneMonthBeforeDateStroke = now.minus({ months: 1 });
const thirtyDaysBeforeDateStroke = now.minus({ days: 30 });
const twoMonthsBeforeDateStroke = now.minus({ months: 2 });
const fiveYearsBeforeDateStroke = now.minus({ years: 5 });

const formatCalendarMetaTitle = (calendarName: string, asOfDate: LuxonDateTime, suffix?: string) => {
  try {
    const todayDateString = now.toFormat('yyyy-MM-dd');
    const asOfDateString = asOfDate.toFormat('yyyy-MM-dd');
    asOfDate = getLastOpenMarketDay(now).date;
    let result = `${calendarName} as of ${asOfDate.toFormat('MMM d').concat(`${getNumberSuffix(asOfDate.day)}`)}`;
    const lessThanLastOpenMarketDay = asOfDateString <= (getLastOpenMarketDay(now).dateRange.date_from as string);
    if (
      asOfDateString === todayDateString &&
      todayDateString !== getLastOpenMarketDay(now).dateRange.date_from &&
      lessThanLastOpenMarketDay
    ) {
      result = result + ` | ${suffix ? suffix : calendarName?.replace('Calendar', '')} Today`;
    }
    return result;
  } catch {
    return calendarName;
  }
};

const AnalystRatingsCalendarData: CalendarDataI = {
  calendar: {
    interval: SevenDaysBeforeInterval,
  },
  calendarDate: new Date(),
  description:
    'Analyst ratings are quantitative and qualitative analysis of a stock by Wall Street stock rating analysts. Stock ratings consist of expected future growth, current stock valuation and macroeconomic trends.',
  loadComponent: loadAnalystRatingCalendar,
  name: 'Analyst Stock Ratings',
  news: {
    params: {
      channels: ['Analyst Ratings'],
      displayOutput: 'abstract',
      pageSize: 10,
      type: 'stories',
    },
    title: 'Recent Analyst Stock Rating News',
  },
  pageId: 90498,
  routes: {
    downgrades: {
      calendarDate: new Date(),
      description:
        'Analyst downgrades are often a bearish signal for a stock. When an analyst downgrades a stock it means the stock rating is lower than it previously was and the analyst likely does not recommend people buy the stock.',
      loadComponent: loadAnalystRatingCalendar,
      name: 'Analyst Stock Ratings',
      news: {
        params: {
          channels: ['Downgrades'],
          displayOutput: 'abstract',
          pageSize: 10,
          type: 'stories',
        },
        title: 'Recent Analyst Stock Rating Downgrades News',
      },
      pageId: 101828,
      title: 'Analyst Stock Ratings Downgrades',
    },
    initiations: {
      description:
        'Analyst initiations occur when coverage begins on a stock. When an analyst initiates coverage, they publish a first rating and price target to help investors understand the opportunity.',
      loadComponent: loadAnalystRatingCalendar,
      name: 'Analyst Stock Ratings',
      news: {
        params: {
          channels: ['Initiation'],
          displayOutput: 'abstract',
          pageSize: 10,
          type: 'stories',
        },
        title: 'Recent Analyst Stock Rating Initiation News',
      },
      pageId: 101833,
      title: 'Analyst Stock Ratings Initiations',
    },
    upgrades: {
      calendarDate: new Date(),
      description:
        "Analyst upgrades are typically bullish for a stock. When there is a stock upgrade, the analysts who rate the stock feel better about the company's future and typically either recommend someone holds or buys the stock.",
      loadComponent: loadAnalystRatingCalendar,
      name: 'Analyst Stock Ratings',
      news: {
        params: {
          channels: ['Upgrades'],
          displayOutput: 'abstract',
          pageSize: 10,
          type: 'stories',
        },
        title: 'Recent Analyst Stock Rating Upgrade News',
      },
      pageId: 101830,
      title: 'Analyst Stock Ratings Upgrades',
    },
  },
  seo: {
    description:
      'From "buy" and "sell" ratings to "equal weight" and "outperform" ratings, you want to pay attention to analyst ratings as a trader or investor.',
  },
  separateTitle: true,
  serverSide: {
    columnDef: AnalystRatingsColumnsDefV2,
    fetchData: (options: ServerSideFetchOptions) => {
      const queryParams = calendarQueryParams(options.params) as RatingsCalendarParams;
      const hasTickers = hasCalendarSymbols(options.params);
      const hasAnalystId = Boolean(queryParams.analystId);
      const params = {
        pageSize: 100,
        ...(hasTickers || hasAnalystId ? {} : formatIntervalQueryParams(getSevenDaysBeforeInterval())),
        ...queryParams,
      };

      return fetchAnalystRatings(options.session, params, false).then((items: Ratings[]) => {
        if (options?.tab) {
          const initiates: Ratings[] = [];
          const downgrades: Ratings[] = [];
          const upgrades: Ratings[] = [];

          if (Array.isArray(items)) {
            items.forEach(item => {
              if (item.action_company === 'Initiates Coverage On') {
                initiates.push(item);
              } else if (item.action_company === 'Downgrades') {
                downgrades.push(item);
              } else if (item.action_company === 'Upgrades') {
                upgrades.push(item);
              }
            });
          }

          switch (options.tab as string) {
            case 'upgrades':
              return upgrades;
            case 'initiations':
              return initiates;
            case 'downgrades':
              return downgrades;
            case 'all':
            default:
              return items;
          }
        } else {
          return items;
        }
      });
    },
  },
  showProCTA: true,
  title: "Today's Analyst Stock Ratings | Upgrades, Downgrades",
};

export const Calendars: { [key in CalendarType]: CalendarDataI } = {
  'analyst-predictions': {
    ...AnalystRatingsCalendarData,
    name: 'Analyst Predictions',
    news: AnalystRatingsCalendarData.news as { title: string; params: NewsFeedQueryParams },
    pageId: 90498,
    routes: {
      downgrades: {
        ...(AnalystRatingsCalendarData?.routes?.downgrades as CalendarDataI),
        title: 'Stock Ratings - Downgrades',
      },
      initiations: {
        ...(AnalystRatingsCalendarData?.routes?.initiations as CalendarDataI),
        title: 'Stock Ratings - Initiations',
      },
      upgrades: {
        ...(AnalystRatingsCalendarData?.routes?.upgrades as CalendarDataI),
        title: 'Stock Ratings - Upgrades',
      },
    },
    seo: {
      canonical: 'https://www.benzinga.com/analyst-stock-ratings',
      description:
        'From "buy" and "sell" ratings to "equal weight" and "outperform" ratings, you want to pay attention to analyst ratings as a trader or investor.',
    },
    serverSide: {
      columnDef: AnalystRatingsColumnsDef,
      fetchData: (options: ServerSideFetchOptions) =>
        AnalystRatingsCalendarData.serverSide?.fetchData?.(options) as Promise<Ratings[]>,
    },
    title: 'Stock Ratings',
  },
  'analyst-ratings': {
    ...AnalystRatingsCalendarData,
    seo: {
      ...AnalystRatingsCalendarData.seo,
      canonical: 'https://www.benzinga.com/analyst-stock-ratings',
    },
  },
  'conference-calls': {
    calendar: {
      interval: OneMonthAfter,
    },
    injectTickersLogos: true,
    loadComponent: loadConferenceCallCalendar,
    name: 'Conference Calls',
    news: {
      params: {
        channels: ['Earnings'],
        pageSize: 10,
      },
      title: 'Recent Earnings News',
    },
    pageId: 101381,
    seo: {
      canonical: 'https://www.benzinga.com/calendars/conference-calls',
      description: 'Stay up to date with upcoming conference calls for publicly traded companies.',
    },
    serverSide: {
      columnDef: ConferenceCallCalendarDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const hasTickers = hasCalendarSymbols(options.params);
        const dateNow = LuxonDateTime.fromFormat(new Date().toISOString().split('T')[0], 'yyyy-MM-dd');
        const oneMonthDateInterval = formatIntervalLastOpenMarketDay(dateNow, dateNow.plus({ months: 1 }));
        const params = {
          ...(hasTickers ? {} : formatIntervalQueryParams(oneMonthDateInterval)),
          ...calendarQueryParams(options.params),
        };
        return fetchConferenceCall(options.session, params, false, 100);
      },
    },
    title: formatCalendarMetaTitle(
      'Conference Calls',
      LuxonDateTime.fromFormat(OneMonthAfter?.date_from as string, 'yyyy-MM-dd'),
    ),
  },
  dividends: {
    calendar: {
      interval: SevenDaysAfterInterval,
    },
    description: `The table below is a list view calendar of stocks with ex-dividend dates as of ${todayDateStroke}. The table is sorted by dividends today. In order for an investor to receive a dividend, they must own a stock, ETF or mutual fund by the ex-dividend date.`,
    loadComponent: loadDividendsCalendar,
    name: 'Dividend',
    news: {
      params: {
        channels: ['Dividends'],
        pageSize: 10,
      },
      title: 'Recent Dividend News',
    },
    pageId: 90241,
    seo: {
      canonical: 'https://www.benzinga.com/dividends',
      description:
        "Learn dividend-paying stocks and pay dates with the latest, most up-to-date market information from Benzinga's dividend calendar",
    },
    serverSide: {
      columnDef: DividendsCalendarColumnsDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const inputParams = (options.params ?? {}) as Record<string, string | number>;
        const hasSymbols = !!inputParams.symbols;
        const dateNow = LuxonDateTime.fromFormat(new Date().toISOString().split('T')[0], 'yyyy-MM-dd');
        const sevenDaysInterval = formatIntervalLastOpenMarketDay(dateNow, dateNow.plus({ days: 7 }));
        const dateFrom = inputParams.dateFrom as string | undefined;
        const dateTo = inputParams.dateTo as string | undefined;
        const shouldApplyDates = Boolean(dateFrom && dateTo) || !hasSymbols;

        return fetchDividends(
          options.session,
          {
            dateSort: (inputParams.dateSort as 'announced' | 'ex' | 'payable' | 'record' | undefined) ?? 'ex',
            ...(shouldApplyDates && {
              dateFrom: (dateFrom ?? sevenDaysInterval.date_from) as string,
              dateTo: (dateTo ?? sevenDaysInterval.date_to) as string,
            }),
            ...(inputParams.symbols && { symbols: String(inputParams.symbols) }),
          },
          false,
          100,
        );
      },
    },
    title: formatCalendarMetaTitle('Dividend Calendar', getLastOpenMarketDay(now).date, 'Dividends'),
  },
  earnings: {
    calendar: {
      fallbackInterval: ThirtyDaysAfterInterval,
      interval: getLastOpenMarketDay(now).dateRange,
    },
    campaignifyUTM: 'campaignify-earnings',
    campaignifyVariant: 'claim-your-free-benzinga-pro-access',
    description:
      'The table below is a calendar list view of publicly traded stocks that have released their earnings reports. This earnings calendar is updated daily, so if the company you are looking for is not here today, either adjust the date range wider or come back and check tomorrow.',
    injectTickersLogos: true,
    loadComponent: loadEarningsCalendar,
    name: 'Earnings',
    news: {
      params: {
        channels: ['Earnings'],
        pageSize: 10,
      },
      title: 'Recent Earnings News',
    },
    pageId: 99752,
    seo: {
      canonical: 'https://www.benzinga.com/earnings',
      description:
        "Keep up with companies' quarterly and annual earnings, along with the latest EPS estimates from each company with Benzinga",
    },
    separateTitle: true,
    serverSide: {
      columnDef: EarningsCalendarColumnsDefinition,
      fetchData: (options: ServerSideFetchOptions) => {
        const hasTickers = hasCalendarSymbols(options.params);
        const dateNow = LuxonDateTime.fromFormat(new Date().toISOString().split('T')[0], 'yyyy-MM-dd');
        const params = {
          ...(hasTickers ? {} : formatIntervalQueryParams(getLastOpenMarketDay(dateNow).dateRange)),
          ...calendarQueryParams(options.params),
        };
        return fetchEarnings(options.session, params, false, 100);
      },
    },
    showProCTA: true,
    title: formatCalendarMetaTitle('Earnings Calendar', getLastOpenMarketDay(now).date),
  },
  economic: {
    calendar: {
      fallbackInterval: SevenDaysAfterInterval,
      interval: SevenDaysBeforeInterval,
    },
    description: '',
    loadComponent: loadEconomicsCalendar,
    name: 'Economic',
    news: {
      params: {
        channels: ['Economics'],
        pageSize: 10,
      },
      title: 'Recent Economic News',
    },
    pageId: 90254,
    seo: {
      canonical: 'https://www.benzinga.com/calendars/economic',
      description:
        'Stay informed about upcoming economic events and indicators with our comprehensive economic calendar.',
    },
    serverSide: {
      columnDef: EconomicsColDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const params = {
          ...formatIntervalQueryParams(SevenDaysBeforeInterval),
          ...calendarQueryParams(options.params),
        };
        return fetchEconomics(options.session, params, 100);
      },
    },
    title: formatCalendarMetaTitle('Economic Calendar', getLastOpenMarketDay(now).date),
  },
  events: {
    calendar: {
      interval: ThreeMonthsForwardInterval,
    },
    description:
      'Corporate events include conference calls, annual meetings, shareholder meetings, summits, analyst days and other scheduled company activities.',
    loadComponent: loadEventsCalendar,
    name: 'Events',
    news: {
      params: {
        channels: ['Events'],
        pageSize: 10,
      },
      title: 'Recent Corporate News',
    },
    pageId: 384834,
    seo: {
      canonical: 'https://www.benzinga.com/calendars/events',
      description:
        'Track upcoming corporate events including conference calls, annual meetings, summits, analyst meetings and other market-moving company events.',
    },
    serverSide: {
      columnDef: EventsCalendarColumnsDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const dateNow = LuxonDateTime.fromFormat(new Date().toISOString().split('T')[0], 'yyyy-MM-dd');
        const threeMonthInterval = formatIntervalLastOpenMarketDay(dateNow, dateNow.plus({ months: 3 }));
        const params = {
          ...formatIntervalQueryParams(threeMonthInterval),
          ...calendarQueryParams(options.params),
        };
        return fetchCompanyEvents(options.session, params, COMPANY_EVENTS_PAGE_SIZE);
      },
    },
    title: formatCalendarMetaTitle(
      'Events Calendar',
      LuxonDateTime.fromFormat(ThreeMonthsForwardInterval.date_from as string, 'yyyy-MM-dd'),
    ),
  },
  fda: {
    breadcrumbName: 'FDA Calendar',
    calendar: {
      interval: ThirtyDaysAfterInterval,
    },
    loadComponent: loadFDACalendar,
    name: 'FDA',
    news: {
      params: {
        pageSize: 5,
        topics: ['FDA Approvals'],
      },
      title: 'Recent FDA Approvals',
    },
    pageId: 105610,
    seo: {
      canonical: 'https://www.benzinga.com/fda-calendar',
      description:
        "Updated daily, the FDA calendar gives you insight into FDA actions on companies and upcoming actions the FDA is expected to take. Benzinga's FDA calendar shows historical FDA data, upcoming dates that companies will be impacted by the FDA and ranges of dates.",
    },
    serverSide: {
      columnDef: FDAColumnsDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const hasTickers = hasCalendarSymbols(options.params);
        const dateNow = LuxonDateTime.fromFormat(new Date().toISOString().split('T')[0], 'yyyy-MM-dd');
        const thirtyDaysInterval = formatIntervalLastOpenMarketDay(dateNow, dateNow.plus({ days: 30 }));
        const params = {
          ...(hasTickers ? {} : formatIntervalQueryParams(thirtyDaysInterval)),
          ...calendarQueryParams(options.params),
        };
        const tabKey: FDACalendarTabKeysType =
          options.tab && ['historical', 'exact-dates', 'estimated-dates', 'pdufa'].includes(options.tab)
            ? (options.tab as FDACalendarTabKeysType)
            : 'historical';
        return fetchFDA(options.session, params, tabKey, 100);
      },
    },
    title: 'FDA Calendar and Recent FDA News',
  },
  guidance: {
    calendar: {
      interval: OneMonthBefore,
    },
    loadComponent: loadGuidanceCalendar,
    name: 'Guidance',
    news: {
      params: {
        channels: ['Guidance'],
        pageSize: 10,
      },
      title: 'Recent Guidance News',
    },
    pageId: 101378,
    seo: {
      canonical: 'https://www.benzinga.com/calendars/guidance',
      description: 'Track company guidance and forward-looking statements with our comprehensive guidance calendar.',
    },
    serverSide: {
      columnDef: GuidanceCalendarColumnsDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const hasTickers = hasCalendarSymbols(options.params);
        const params = {
          ...(hasTickers ? {} : formatIntervalQueryParams(OneMonthBefore)),
          ...calendarQueryParams(options.params),
        };
        return fetchGuidance(options.session, params, false, 100);
      },
    },
    title: formatCalendarMetaTitle('Guidance Calendar', oneMonthBeforeDateStroke),
  },
  'insider-trades': {
    calendar: {
      interval: FiveYearsBeforeInterval,
    },
    calendarDate: new Date(),
    description:
      'The table below is a calendar list view of insider transactions amounts, trade dates and executives completing transactions. Insider trades include the CEO, CFO, board of directors and is updated today.',
    loadComponent: loadInsiderTradesCalendar,
    name: 'Insider Trades',
    news: null,
    pageId: 281175, // Need to review
    seo: {
      canonical: 'https://www.benzinga.com/calendars/insider-trades',
      description: 'Track insider buying and selling activity with our comprehensive insider trades calendar.',
    },
    serverSide: {
      columnDef: InsiderTradesCalendarColumnsDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const dateRange = formatIntervalLastOpenMarketDay(fiveYearsBeforeDateStroke, now);
        const queryParams = {
          ...formatIntervalQueryParams(dateRange),
          ...calendarQueryParams(options.params),
        };
        const params: InsiderTradesParams = {
          dateRange: {
            date_from: new Date(String(queryParams.dateFrom || dateRange.date_from || '')),
            date_to: new Date(String(queryParams.dateTo || dateRange.date_to || '')),
          },
          symbol: symbolFromQueryParams(queryParams),
        };
        return fetchInsiderTrades(options.session, params);
      },
    },
    title: formatCalendarMetaTitle('Insider Trades', getLastOpenMarketDay(now).date),
  },
  'insider-trades-networth': {
    calendar: {
      interval: FiveYearsBeforeInterval,
    },
    calendarDate: new Date(),
    description:
      'The table below is a calendar list view of insider transactions amounts, trade dates and executives completing transactions. Insider trades include the CEO, CFO, board of directors and is updated today.',
    loadComponent: loadInsiderTradesCalendar,
    name: 'Insider Trades Net Worth',
    news: null,
    pageId: 281175, // Need to review
    serverSide: {
      columnDef: InsiderTradesNetWorthColumnsDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const dateRange = formatIntervalLastOpenMarketDay(fiveYearsBeforeDateStroke, now);
        const queryParams = {
          ...formatIntervalQueryParams(dateRange),
          ...calendarQueryParams(options.params),
        };
        const params: InsiderTradesParams = {
          dateRange: {
            date_from: new Date(String(queryParams.dateFrom || dateRange.date_from || '')),
            date_to: new Date(String(queryParams.dateTo || dateRange.date_to || '')),
          },
          symbol: symbolFromQueryParams(queryParams),
        };
        return fetchInsiderTrades(options.session, params);
      },
    },
    title: formatCalendarMetaTitle('Insider Trades', getLastOpenMarketDay(now).date),
  },
  'insider-trades-v2': {
    calendar: {
      interval: FiveYearsBeforeInterval,
    },
    calendarDate: new Date(),
    description:
      'The table below is a calendar list view of insider transactions amounts, trade dates and executives completing transactions. Insider trades include the CEO, CFO, board of directors and is updated today.',
    loadComponent: loadInsiderTradesCalendar,
    name: 'Insider Trades',
    news: null,
    pageId: 281175, // Need to review
    seo: {
      canonical: 'https://www.benzinga.com/calendars/insider-trades',
      description: 'Track insider buying and selling activity with our comprehensive insider trades calendar.',
    },
    serverSide: {
      colorRowByValue: true,
      colorRowField: 'traded_shares',
      columnDef: InsiderTradesCalendarColumnsDefV2,
      fetchData: (options: ServerSideFetchOptions) => {
        const dateRange = formatIntervalLastOpenMarketDay(fiveYearsBeforeDateStroke, now);
        const queryParams = {
          ...formatIntervalQueryParams(dateRange),
          ...calendarQueryParams(options.params),
        };
        const params: InsiderTradesParams = {
          dateRange: {
            date_from: new Date(String(queryParams.dateFrom || dateRange.date_from || '')),
            date_to: new Date(String(queryParams.dateTo || dateRange.date_to || '')),
          },
          symbol: symbolFromQueryParams(queryParams),
        };
        return fetchInsiderTrades(options.session, params);
      },
    },
    title: formatCalendarMetaTitle('Insider Trades Net Worth', getLastOpenMarketDay(now).date),
  },
  ipos: {
    calendar: {
      fallbackInterval: TwoMonthsAfterInterval,
      interval: ThirtyDaysAfterInterval,
    },
    loadComponent: loadIPOCalendar,
    name: 'IPO',
    news: {
      params: {
        channels: ['IPOs'],
        pageSize: 10,
      },
      title: 'Recent News',
    },
    pageId: 94564,
    presentedByImage: 'https://cdnwp-s3.benzinga.com/wp-content/uploads/2026/06/08122450/sofi-logo.png',
    seo: {
      canonical: 'https://www.benzinga.com/calendars/ipos',
      description:
        "An initial public offering (IPO) is a company's first entry into the public stock market. Keep track of which companies are going public and when.",
    },
    serverSide: {
      columnDef: IPOColumnsDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const hasTickers = hasCalendarSymbols(options.params);
        const dateNow = LuxonDateTime.fromFormat(new Date().toISOString().split('T')[0], 'yyyy-MM-dd');
        const thirtyDaysInterval = formatIntervalLastOpenMarketDay(dateNow, dateNow.plus({ days: 30 }));
        const params = {
          ...(hasTickers ? {} : formatIntervalQueryParams(thirtyDaysInterval)),
          ...calendarQueryParams(options.params),
        };
        return fetchIPO(options.session, params, false, 100);
      },
    },
    title: formatCalendarMetaTitle('IPO Calendar', getLastOpenMarketDay(now).date),
  },
  'm-a': {
    calendar: {
      interval: TwoMonthsBefore,
    },
    loadComponent: loadMACalendar,
    name: 'M&A',
    news: {
      params: {
        channels: ['M&A'],
        pageSize: 10,
      },
      title: 'Recent M&A News',
    },
    pageId: 101385,
    seo: {
      canonical: 'https://www.benzinga.com/calendars/m-a',
      description: 'Stay informed about mergers and acquisitions with our comprehensive M&A calendar.',
    },
    serverSide: {
      columnDef: MACalendarColumnsDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const hasTickers = hasCalendarSymbols(options.params);
        const params = {
          ...(hasTickers ? {} : formatIntervalQueryParams(TwoMonthsBefore)),
          ...calendarQueryParams(options.params),
        };
        return fetchMA(options.session, params, 100);
      },
    },
    title: formatCalendarMetaTitle('Merger and Acquisitions', twoMonthsBeforeDateStroke, '(M&A)'),
  },
  'short-interest': {
    calendar: {
      interval: SevenDaysBeforeInterval,
    },
    calendarDate: new Date(),
    description: '',
    loadComponent: loadShortInterestCalendar,
    name: 'Short Interest',
    news: null,
    pageId: 281175, // Need to review
    seo: {
      canonical: 'https://www.benzinga.com/calendars/short-interest',
      description: 'Track short interest data for stocks with our comprehensive short interest calendar.',
    },
    serverSide: {
      columnDef: ShortInterestCalendarColumnDef,
      fetchData: async (options: ServerSideFetchOptions) => {
        const dateNow = LuxonDateTime.fromFormat(new Date().toISOString().split('T')[0], 'yyyy-MM-dd');
        const oneMonthDateInterval = formatIntervalLastOpenMarketDay(dateNow.minus({ months: 1 }), dateNow);
        const queryParams = {
          ...formatIntervalQueryParams(oneMonthDateInterval),
          ...calendarQueryParams(options.params),
        };
        const params = {
          dateFrom: toQueryDateString(queryParams.dateFrom, oneMonthDateInterval.date_from),
          dateTo: toQueryDateString(queryParams.dateTo, oneMonthDateInterval.date_to),
          symbols: queryParams.symbols
            ? Array.isArray(queryParams.symbols)
              ? queryParams.symbols
              : [queryParams.symbols]
            : undefined,
        };
        const result = await fetchShortInterest(options.session, params);
        if (result.err) {
          throw result.err;
        }
        return result.ok;
      },
    },
    title: formatCalendarMetaTitle('Short Interest', getLastOpenMarketDay(now).date),
  },
  spac: {
    calendar: {
      interval: ThirtyDaysBeforeInterval,
    },
    loadComponent: loadSpacCalendar,
    name: 'SPAC',
    news: {
      params: {
        channels: ['SPAC'],
        pageSize: 10,
      },
      title: 'Recent SPAC News',
    },
    pageId: 105605,
    seo: {
      canonical: 'https://www.benzinga.com/calendars/spac',
      description:
        'Stay informed about Special Purpose Acquisition Companies (SPACs) with our comprehensive SPAC calendar.',
    },
    serverSide: {
      columnDef: SPACColumnsDefinition,
      fetchData: (options: ServerSideFetchOptions) => {
        const hasTickers = hasCalendarSymbols(options.params);
        const params = {
          ...(hasTickers ? {} : formatIntervalQueryParams(ThirtyDaysBeforeInterval)),
          ...calendarQueryParams(options.params),
        };
        return fetchSpac(options.session, params, false, 100);
      },
    },
    title: formatCalendarMetaTitle('SPAC Calendar', thirtyDaysBeforeDateStroke),
  },
  'stock-splits': {
    calendar: {
      interval: formatInterval(sevenDaysBeforeDateStroke, now.plus({ days: 60 })),
    },
    loadComponent: loadStockSplitsCalendar,
    name: 'Stock Splits',
    news: {
      params: {
        channels: ['Stock Split'],
        pageSize: 10,
      },
      title: 'Upcoming Stock Split News',
    },
    pageId: 99749,
    seo: {
      canonical: 'https://www.benzinga.com/calendars/stock-splits',
      description:
        'Learn which recent and upcoming company shares will split and when in this regularly updated stocks splits calendar from Benzinga',
    },
    serverSide: {
      columnDef: StockSplitsColumnsDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const hasTickers = hasCalendarSymbols(options.params);
        const defaultInterval = formatInterval(sevenDaysBeforeDateStroke, now.plus({ days: 60 }));
        const params = {
          dateSearchField: 'ex' as const,
          ...(hasTickers ? {} : formatIntervalQueryParams(defaultInterval)),
          ...calendarQueryParams(options.params),
        } as SplitsCalendarParams;
        return fetchStockSplits(options.session, params, false, 100);
      },
    },
    title: formatCalendarMetaTitle('Upcoming Stock Splits', sevenDaysBeforeDateStroke),
  },
  'unusual-options-activity': {
    calendar: {
      interval: SevenDaysBeforeInterval,
    },
    calendarDate: new Date(),
    description:
      'The table below is a calendar list view of unusual options activity which shows contracts which are trading at abnormal volume levels or price levels. Traders typically use unusual options activity data to identify especially bullish or bearish bets made with high dollar amounts.',
    loadComponent: loadOptionsCalendar,
    name: 'Options',
    news: {
      params: {
        channels: ['Options'],
        pageSize: 10,
        topics: ['Unusual Options Activity'],
      },
      title: 'Options Activity',
    },
    pageId: 98763,
    seo: {
      canonical: 'https://www.benzinga.com/calendars/unusual-options-activity',
      description:
        'Unusual options activity shows contracts which are trading at abnormal volume levels or price levels. Traders typically use unusual options activity data to identify especially bullish or bearish bets made with high dollar amounts.',
    },
    serverSide: {
      columnDef: OptionsCalendarColumnsDef,
      fetchData: (options: ServerSideFetchOptions) => {
        const urlParams = calendarQueryParams(options.params);
        const hasTickers = hasCalendarSymbols(options.params);
        const defaultDates = { dateFrom: SevenDaysBeforeInterval.date_from, dateTo: SevenDaysBeforeInterval.date_to };
        const params = {
          ...(hasTickers ? {} : defaultDates),
          ...(urlParams.dateFrom && { dateFrom: toQueryDateString(urlParams.dateFrom) }),
          ...(urlParams.dateTo && { dateTo: toQueryDateString(urlParams.dateTo) }),
          ...(urlParams.symbols && {
            symbols: Array.isArray(urlParams.symbols) ? urlParams.symbols : [urlParams.symbols],
          }),
        };
        return fetchOptions(options.session, params, 100);
      },
    },
    title: formatCalendarMetaTitle('Unusual Options Activity', getLastOpenMarketDay(now).date),
  },
};
